<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, computed, onMounted } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { leaveReport as leaveReportConfig } from "./data";
import LeaveReportTable from "./leave-report-table.vue";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";
import adminLeaveService from "@/services/hrm/adminLeaveService";
import { toast } from "@/utils/toast";

const title = "Leave Report";
const text = "HR";
const text1 = "Leave Report";

const store = useAdminLeaveStore();

const dateRangeInput = ref(null);
const sortLabel = ref("Last 7 Days");
const statusLabel = ref("Select Status");
const typeLabel = ref("All Types");

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

// ----- Stats helpers -----
const totalLeaves = computed(() => {
  const s = store.stats || {};
  return (
    (s.total_pending || 0) + (s.total_approved || 0) + (s.total_declined || 0)
  );
});

function applyRange(start, end) {
  const fmt = "YYYY-MM-DD";
  store.filters.date_from = start.format(fmt);
  store.filters.date_to = end.format(fmt);

  if (dateRangeInput.value) {
    dateRangeInput.value.value = `${start.format("DD/MM/YYYY")} - ${end.format(
      "DD/MM/YYYY"
    )}`;
  }

  reload({ page: 1 });
}

function onSearchEnter() {
  reload({ page: 1 });
}

// ----- Exports -----
async function exportPdf() {
  try {
    const params = { ...store.filters };
    const res = await adminLeaveService.exportPdf(params);

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `leave-report-${new Date().toISOString().slice(0, 10)}.pdf`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to export PDF", "error");
  }
}

async function exportExcel() {
  try {
    const params = { ...store.filters };
    const res = await adminLeaveService.exportCsv(params);

    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `leave-report-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to export Excel", "error");
  }
}

// Year filter for chart
const selectedYear = ref(new Date().getFullYear());

// Date range picker
function makeRangeLabel(start, end) {
  const s = start.format("DD/MM/YYYY");
  const e = end.format("DD/MM/YYYY");
  return `${s} - ${e}`;
}

function initDateRangePicker() {
  if (!dateRangeInput.value) return;

  const start = moment().subtract(6, "days");
  const end = moment();

  const picker = new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    },
    (start, end) => {
      dateRangeInput.value.value = makeRangeLabel(start, end);
      store.filters.date_from = start.format("YYYY-MM-DD");
      store.filters.date_to = end.format("YYYY-MM-DD");
      reload();
    }
  );

  // Initialize label + filters
  dateRangeInput.value.value = makeRangeLabel(start, end);
  store.filters.date_from = start.format("YYYY-MM-DD");
  store.filters.date_to = end.format("YYYY-MM-DD");
}

// Dynamic legend from chart series / fallback
const legendItems = computed(() => {
  const src =
    store.chartSeries && store.chartSeries.length
      ? store.chartSeries
      : [];

  return src.map((s, idx) => {
    let colorClass = "text-primary";
    if (idx === 0) colorClass = "text-success";
    else if (idx === 1) colorClass = "text-warning";
    else if (idx === 2) colorClass = "text-dark";
    return {
      name: s.name || `Type ${idx + 1}`,
      colorClass,
    };
  });
});

// Dynamic years for "This Year" dropdown
const chartYears = computed(() => {
  if (store.chartYears && store.chartYears.length) {
    return store.chartYears;
  }
  const y = selectedYear.value;
  return [y, y - 1, y - 2];
});

// Apexchart config using dynamic series/categories
const leaveChart = computed(() => {
  const series =
    store.chartSeries && store.chartSeries.length
      ? store.chartSeries
      : leaveReportConfig.series;
  const categories =
    store.chartCategories && store.chartCategories.length
      ? store.chartCategories
      : leaveReportConfig.sline.xaxis.categories;

  return {
    options: {
      ...leaveReportConfig.sline,
      xaxis: {
        ...(leaveReportConfig.sline.xaxis || {}),
        categories,
      },
    },
    series,
  };
});

const REPORT_STATUS_LABELS = {
  New: "Pending Manager",
  ManagerApproved: "Awaiting HR",
  Approved: "Approved",
  Declined: "Declined",
  Cancelled: "Cancelled",
};

const statusFilterLabel = computed(() =>
  store.filters.status
    ? `Status: ${REPORT_STATUS_LABELS[store.filters.status] || store.filters.status}`
    : "All Statuses"
);

// Status dropdown
function setStatus(status, label) {
  store.filters.status = status;
  statusLabel.value = label;
  reload();
}

// Year dropdown click
function selectYear(y) {
  selectedYear.value = y;
  store.fetchChart({ year: y });
}

// Simple type options from loaded data (dynamic leave types)
const typeOptions = computed(() => {
  const names = new Map(); // id/name -> label
  (store.items || []).forEach((r) => {
    const t = r.leaveType || r.leave_type;
    if (t?.id) {
      if (!names.has(t.id)) {
        names.set(t.id, t.name || t.code || `Type #${t.id}`);
      }
    }
  });

  return Array.from(names.entries()).map(([id, label]) => ({
    id,
    label,
  }));
});

function setType(typeId, label) {
  store.filters.type_id = typeId || "";
  typeLabel.value = label;
  reload();
}

// Sort dropdown
function setSort(key, label) {
  sortLabel.value = label;
  if (key === "recent") {
    store.filters.sort = "recent";
  } else if (key === "oldest") {
    store.filters.sort = "oldest";
  } else if (key === "last7") {
    const start = moment().subtract(6, "days");
    const end = moment();
    store.filters.date_from = start.format("YYYY-MM-DD");
    store.filters.date_to = end.format("YYYY-MM-DD");
  } else if (key === "lastMonth") {
    const start = moment().subtract(1, "month").startOf("month");
    const end = moment().subtract(1, "month").endOf("month");
    store.filters.date_from = start.format("YYYY-MM-DD");
    store.filters.date_to = end.format("YYYY-MM-DD");
  }
  reload();
}

// Table pagination change
function onTableChange(pagination) {
  store.filters.page = pagination.current;
  store.filters.per_page = pagination.pageSize;
  store.fetchAll();
}

// Export PDF / Excel – same pattern as project report
async function downloadReport(format = "csv") {
  try {
    const res = await store.exportReport(format);

    const mime =
      format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const blob = new Blob([res.data], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const ext = format === "pdf" ? "pdf" : "csv";
    link.href = url;
    link.setAttribute(
      "download",
      `leave-report-${new Date().toISOString().slice(0, 10)}.${ext}`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e.message ||
      "Failed to export leave report";
    toast(msg, "error");
  }
}

// Initial load
async function reload() {
  store.filters.page = 1;
  await Promise.all([
    store.fetchStats(),
    store.fetchAll(),
    store.fetchChart({ year: selectedYear.value }),
  ]);
}

onMounted(() => {
  initDateRangePicker();
  reload();
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="downloadReport('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="downloadReport('csv')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Top cards + chart -->
      <div class="row dq-stat-grid">
        <!-- Stats cards -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill">
            <!-- Total Leaves -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2 overflow-hidden"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Total Leaves
                      </p>
                      <h4>{{ totalLeaves }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-primary bg-transparent-primary rounded-circle d-flex align-items-center justify-content-center"
                        >
                          <i class="ti ti-calendar-x text-primary"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <!-- <div class="p-2 bg-gray-100 br-5">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <p class="fs-12 fw-normal mb-0">Last Month</p>
                      <span
                        class="fs-12 fw-normal text-success d-flex align-items-center"
                      >
                        <i
                          class="ti ti-arrow-wave-right-up text-success me-1"
                        ></i
                        >+17.02%
                      </span>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>

            <!-- Approved -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2 overflow-hidden"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Approved Leaves
                      </p>
                      <h4>{{ store.stats.total_approved }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-success bg-transparent-success rounded-circle d-flex align-items-center justify-content-center"
                        >
                          <i class="ti ti-calendar-x text-success"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <!-- <div class="p-2 bg-gray-100 br-5">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <p class="fs-12 fw-normal mb-0">Last Month</p>
                      <span
                        class="fs-12 fw-normal text-success d-flex align-items-center"
                      >
                        <i
                          class="ti ti-arrow-wave-right-up text-success me-1"
                        ></i
                        >+17.02%
                      </span>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>

            <!-- Pending -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2 overflow-hidden"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Pending Requests
                      </p>
                      <h4>{{ store.stats.total_pending }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-skyblue bg-transparent-skyblue rounded-circle d-flex align-items-center justify-content-center"
                        >
                          <i class="ti ti-calendar-x text-skyblue"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <!-- <div class="p-2 bg-gray-100 br-5">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <p class="fs-12 fw-normal mb-0">Last Month</p>
                      <span
                        class="fs-12 fw-normal text-success d-flex align-items-center"
                      >
                        <i
                          class="ti ti-arrow-wave-right-up text-success me-1"
                        ></i
                        >+17.02%
                      </span>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>

            <!-- Declined -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2 overflow-hidden"
                  >
                    <div>
                      <p class="fs-12 fw-normal mb-1 text-truncate">
                        Declined Leaves
                      </p>
                      <h4>{{ store.stats.total_declined }}</h4>
                    </div>
                    <div class="leave-report-icon">
                      <a href="javascript:void(0);">
                        <span
                          class="p-2 border border-danger bg-transparent-danger rounded-circle d-flex align-items-center justify-content-center"
                        >
                          <i class="ti ti-calendar-x text-danger"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <!-- <div class="p-2 bg-gray-100 br-5">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <p class="fs-12 fw-normal mb-0">Last Month</p>
                      <span
                        class="fs-12 fw-normal text-success d-flex align-items-center"
                      >
                        <i
                          class="ti ti-arrow-wave-right-up text-success me-1"
                        ></i
                        >+17.02%
                      </span>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>
            <!-- /cards -->
          </div>
        </div>

        <!-- Chart -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center row-gap-2"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-bar text-danger"></i
                  ></span>
                  <h5>Leaves</h5>
                </div>

                <!-- Dynamic legend -->
                <div class="d-flex align-items-center flex-wrap">
                  <p
                    v-for="(item, idx) in legendItems"
                    :key="idx"
                    class="d-inline-flex align-items-center mb-0 me-2"
                  >
                    <i
                      class="ti ti-square-filled fs-12 me-2"
                      :class="item.colorClass"
                    ></i>
                    {{ item.name }}
                  </p>
                </div>

                <!-- Dynamic year dropdown -->
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYear }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li v-for="y in chartYears" :key="y">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="selectYear(y)"
                      >
                        {{ y }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="card-body py-0">
              <div id="leave-report">
                <apexchart
                  type="bar"
                  height="210"
                  :options="leaveChart.options"
                  :series="leaveChart.series"
                ></apexchart>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Leave list -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Leave Requests</h5>

          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <!-- Date range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Search -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control"
                  v-model.trim="store.filters.q"
                  @keyup.enter="onSearchEnter"
                  placeholder="Search employee / leave type"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-search"></i>
                </span>
              </div>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ statusFilterLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('')"
                    >All</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('New', 'Pending Manager')"
                    >Pending Manager</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('ManagerApproved', 'Awaiting HR')"
                    >Awaiting HR</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Approved', 'Approved')"
                    >Approved</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Declined', 'Declined')"
                    >Declined</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatus('Cancelled', 'Cancelled')"
                    >Cancelled</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort:
                {{
                  store.filters.sort === "oldest" ? "Oldest First" : "Recent"
                }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent')"
                    >Recent</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest')"
                    >Oldest First</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <leave-report-table
              :rows="store.items"
              :loading="store.loading"
              :total="store.pagination.total"
              :page-size="store.pagination.per_page"
              :current="store.pagination.current_page"
              @change="onTableChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


